
import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

interface Point {
  x: number;
  y: number;
}

const GraphingCalculator = () => {
  const [equation, setEquation] = useState<string>('x^2');
  const [xMin, setXMin] = useState<number>(-10);
  const [xMax, setXMax] = useState<number>(10);
  const [step, setStep] = useState<number>(0.5);
  const [data, setData] = useState<Point[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [lineColor, setLineColor] = useState<string>('#2563eb');

  const evaluateExpression = (expression: string, x: number): number => {
    try {
      // Replace common math expressions with JavaScript equivalents
      const jsExpression = expression
        .replace(/\^/g, '**')                  // Replace ^ with ** for exponentiation
        .replace(/sin\(/g, 'Math.sin(')        // Replace sin with Math.sin
        .replace(/cos\(/g, 'Math.cos(')        // Replace cos with Math.cos
        .replace(/tan\(/g, 'Math.tan(')        // Replace tan with Math.tan
        .replace(/sqrt\(/g, 'Math.sqrt(')      // Replace sqrt with Math.sqrt
        .replace(/log\(/g, 'Math.log10(')      // Replace log with Math.log10
        .replace(/ln\(/g, 'Math.log(')         // Replace ln with Math.log
        .replace(/abs\(/g, 'Math.abs(')        // Replace abs with Math.abs
        .replace(/\be\b/g, 'Math.E')           // Replace e constant with Math.E
        .replace(/\bpi\b/g, 'Math.PI');        // Replace pi constant with Math.PI

      // eslint-disable-next-line no-new-func
      const func = new Function('x', `return ${jsExpression}`);
      return func(x);
    } catch (err) {
      setError(`Error evaluating expression: ${err}`);
      return NaN;
    }
  };

  const generateData = () => {
    setError(null);
    
    try {
      const points: Point[] = [];
      for (let x = xMin; x <= xMax; x += step) {
        const y = evaluateExpression(equation, x);
        
        if (!isNaN(y) && isFinite(y)) {
          points.push({ x, y });
        }
      }
      
      if (points.length === 0) {
        setError('No valid points generated for this equation.');
      } else {
        setData(points);
      }
    } catch (err) {
      setError(`Error generating graph data: ${err}`);
    }
  };

  // Generate data initially
  useEffect(() => {
    generateData();
  }, []);

  return (
    <div className="space-y-6">
      <Card>
        <CardContent className="p-6">
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="equation">Equation (y = )</Label>
              <Input
                id="equation"
                value={equation}
                onChange={(e) => setEquation(e.target.value)}
                placeholder="e.g. x^2, sin(x), 2*x+5"
              />
              <p className="text-xs text-gray-500">
                Supported: +, -, *, /, ^, sin(), cos(), tan(), sqrt(), log(), ln(), abs(), e, pi
              </p>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="xMin">X Min</Label>
                <Input
                  id="xMin"
                  type="number"
                  value={xMin}
                  onChange={(e) => setXMin(Number(e.target.value))}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="xMax">X Max</Label>
                <Input
                  id="xMax"
                  type="number"
                  value={xMax}
                  onChange={(e) => setXMax(Number(e.target.value))}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="step">Step</Label>
                <Input
                  id="step"
                  type="number"
                  value={step}
                  onChange={(e) => setStep(Number(e.target.value))}
                  min="0.01"
                  max="1"
                  step="0.01"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Line Color</Label>
              <Select value={lineColor} onValueChange={setLineColor}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="#2563eb">Blue</SelectItem>
                  <SelectItem value="#dc2626">Red</SelectItem>
                  <SelectItem value="#16a34a">Green</SelectItem>
                  <SelectItem value="#9333ea">Purple</SelectItem>
                  <SelectItem value="#ea580c">Orange</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <Button className="w-full" onClick={generateData}>Plot Graph</Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-6">
          {error ? (
            <div className="p-4 bg-red-50 border border-red-200 rounded-md text-red-600">
              {error}
            </div>
          ) : (
            <div className="h-[400px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  data={data}
                  margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="x" 
                    domain={[xMin, xMax]} 
                    type="number" 
                    label={{ value: 'X', position: 'insideBottomRight', offset: -5 }} 
                  />
                  <YAxis 
                    label={{ value: 'Y', angle: -90, position: 'insideLeft' }} 
                  />
                  <Tooltip formatter={(value: number) => value.toFixed(2)} />
                  <Legend />
                  <Line 
                    type="monotone" 
                    dataKey="y" 
                    stroke={lineColor}
                    name={`y = ${equation}`} 
                    dot={false} 
                    isAnimationActive={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default GraphingCalculator;
